import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { CalendarMonthCarousel } from '@/components/CalendarMonthCarousel';
import { CalResourceCombo } from '@/components/CalResourceCombo';
import { BookingDetailSheet } from '@/components/BookingDetailSheet';
import { BookingListCard } from '@/components/BookingListCard';
import { ScreenBackButton } from '@/components/ScreenBackButton';
import { downloadMonthBookingsPdf } from '@/calendar/monthBookingsPdf';
import type { PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';
import {
  CAL_FILTER_FREE,
  dateKeyFromDate,
  formatMonthHeader,
  formatSelectedDayLabel,
  getBookingsForDay,
  getFreeResourcesForDay,
  navigateMonth,
  preferInternalBookings,
} from '@/calendar/pwaCalendarUtils';
import { resolveBrandLogoSrc } from '@/lib/brandLogo';
import type { AppShellOutletContext } from '@/pages/AppShell';

type CalendarResourceOption = {
  id: string;
  name: string;
  color?: string | null;
  agentId: string;
  agentName: string;
  category?: string;
  checkInTime?: string | null;
  checkOutTime?: string | null;
};

export function CalendarPage() {
  const { hasCalendar } = useOutletContext<AppShellOutletContext>();
  const nav = useNavigate();
  const { get, organizationName, organizationLogoUrl } = useSession();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(() => now.getFullYear());
  const [month, setMonth] = useState(() => now.getMonth() + 1);
  const [bookings, setBookings] = useState<PwaCalendarBooking[]>([]);
  const [resources, setResources] = useState<CalendarResourceOption[]>([]);
  const [resourceFilter, setResourceFilter] = useState('');
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [detailBooking, setDetailBooking] = useState<PwaCalendarBooking | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const showFreeOnly = resourceFilter === CAL_FILTER_FREE;

  const loadMonth = useCallback(async () => {
    setLoadingMonth(true);
    setLoadErr(null);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (resourceFilter && resourceFilter !== CAL_FILTER_FREE) {
        params.set('resourceId', resourceFilter);
      }
      const data = await get<{ bookings: PwaCalendarBooking[] }>(
        `/api/console/calendario/bookings?${params.toString()}`
      );
      setBookings(data.bookings.filter((booking) => booking.status === 'confirmed'));
    } catch (e) {
      setBookings([]);
      setLoadErr(e instanceof ApiError ? e.message : 'No se pudieron cargar las reservas');
    } finally {
      setLoadingMonth(false);
    }
  }, [get, year, month, resourceFilter]);

  useEffect(() => {
    if (hasCalendar !== true) return;
    let cancelled = false;
    void get<{ resources: CalendarResourceOption[] }>('/api/console/calendario/resources')
      .then((data) => {
        if (!cancelled) setResources(data.resources ?? []);
      })
      .catch(() => {
        if (!cancelled) setResources([]);
      });
    return () => {
      cancelled = true;
    };
  }, [get, hasCalendar]);

  useEffect(() => {
    if (hasCalendar !== true) return;
    const t = window.setTimeout(() => void loadMonth(), 0);
    return () => clearTimeout(t);
  }, [hasCalendar, loadMonth]);

  const onMonthChange = useCallback(
    (dir: 'prev' | 'next') => {
      const n = navigateMonth(year, month, dir);
      setYear(n.year);
      setMonth(n.month);
      setSelectedDay(null);
    },
    [year, month]
  );

  function openDay(date: Date) {
    if (selectedDay && dateKeyFromDate(selectedDay) === dateKeyFromDate(date)) {
      setSelectedDay(null);
    } else {
      setSelectedDay(date);
    }
  }

  function openDayFullPage(date: Date) {
    nav(`/calendar/reservas/${dateKeyFromDate(date)}`);
  }

  const dayBookings = useMemo(() => {
    if (!selectedDay || showFreeOnly) return [];
    return preferInternalBookings(getBookingsForDay(bookings, selectedDay));
  }, [bookings, selectedDay, showFreeOnly]);

  const dayFreeResources = useMemo(() => {
    if (!selectedDay || !showFreeOnly) return [];
    return getFreeResourcesForDay(resources, bookings, selectedDay);
  }, [selectedDay, showFreeOnly, resources, bookings]);

  const filteredResourceName = useMemo(() => {
    if (!resourceFilter || resourceFilter === CAL_FILTER_FREE) return null;
    return resources.find((resource) => resource.id === resourceFilter)?.name ?? null;
  }, [resourceFilter, resources]);

  async function downloadMonthPdf() {
    setPdfBusy(true);
    setPdfErr(null);
    try {
      const pdfResources =
        resourceFilter && resourceFilter !== CAL_FILTER_FREE
          ? resources.filter((resource) => resource.id === resourceFilter)
          : resources;
      await downloadMonthBookingsPdf({
        companyName: organizationName?.trim() || 'Empresa',
        logoSrc: resolveBrandLogoSrc(organizationLogoUrl),
        monthLabel: formatMonthHeader(year, month),
        year,
        month,
        resourceLabel: filteredResourceName,
        resources: pdfResources.map((resource) => ({
          id: resource.id,
          name: resource.name,
          color: resource.color ?? null,
        })),
        bookings,
      });
    } catch (e) {
      setPdfErr(e instanceof Error && e.message ? e.message : 'No se pudo generar el PDF');
    } finally {
      setPdfBusy(false);
    }
  }

  if (hasCalendar === null) {
    return <p className="pwa-muted">Cargando…</p>;
  }

  if (hasCalendar === false) {
    return (
      <div className="cal-page">
        <div className="pwa-screen-head">
          <ScreenBackButton to="/" />
          <h1 className="pwa-screen-head__title">Calendario</h1>
        </div>
        <p className="pwa-muted">
          No hay agentes con el plugin de calendario activo en tu organización.
        </p>
      </div>
    );
  }

  const today = new Date();

  return (
    <div className="cal-page">
      {pdfErr ? <p className="pwa-err">{pdfErr}</p> : null}
      <CalResourceCombo
        resources={resources}
        value={resourceFilter}
        onChange={(next) => {
          setResourceFilter(next);
          setSelectedDay(null);
          setDetailBooking(null);
        }}
      />
      <div className="cal-pdf-row">
        <button
          type="button"
          className="cal-pdf-btn"
          disabled={pdfBusy || loadingMonth}
          onClick={() => void downloadMonthPdf()}
        >
          <PdfIcon />
          {pdfBusy ? 'Generando PDF…' : 'PDF del mes'}
        </button>
      </div>
      <div className="cal-scroll">
        <CalendarMonthCarousel
          year={year}
          month={month}
          bookings={bookings}
          resources={resources}
          showFreeOnly={showFreeOnly}
          loadingMonth={loadingMonth}
          loadErr={loadErr}
          today={today}
          selectedDay={selectedDay}
          onMonthChange={onMonthChange}
          onOpenDay={openDay}
        />

        {selectedDay && (
          <div className="cal-day-detail">
            <div className="cal-day-detail__header">
              <span className="cal-day-detail__title">{formatSelectedDayLabel(selectedDay)}</span>
              <div className="cal-day-detail__actions">
                {!showFreeOnly ?
                  <button
                    type="button"
                    className="cal-day-detail__expand"
                    onClick={() => openDayFullPage(selectedDay)}
                    aria-label="Ver página completa"
                  >
                    ↗
                  </button>
                : null}
                <button
                  type="button"
                  className="cal-day-detail__close"
                  onClick={() => setSelectedDay(null)}
                  aria-label="Cerrar detalle del día"
                >
                  ×
                </button>
              </div>
            </div>
            {showFreeOnly ?
              dayFreeResources.length === 0 ?
                <p className="cal-day-detail__empty">Sin recursos libres este día</p>
              : <ul className="cal-free-list">
                  {dayFreeResources.map((r) => (
                    <li key={r.id} className="cal-free-list__item">
                      <span
                        className="cal-free-list__dot"
                        style={{
                          background:
                            r.color && /^#[0-9A-Fa-f]{6}$/.test(r.color) ? r.color : '#059669',
                        }}
                        aria-hidden
                      />
                      <div className="cal-free-list__body">
                        <strong>{r.name}</strong>
                        <span>{r.agentName}</span>
                      </div>
                      <span className="cal-free-list__badge">Libre</span>
                    </li>
                  ))}
                </ul>

            : dayBookings.length === 0 ?
              <p className="cal-day-detail__empty">Sin reservas este día</p>
            : <div className="cal-day-detail__list bookings-search-page__list">
                {dayBookings.map((b) => (
                  <BookingListCard key={b.id} booking={b} onOpen={() => setDetailBooking(b)} />
                ))}
              </div>
            }
          </div>
        )}
      </div>

      {detailBooking ? (
        <BookingDetailSheet
          summary={detailBooking}
          onClose={() => setDetailBooking(null)}
          onChanged={() => {
            void loadMonth();
          }}
        />
      ) : null}
    </div>
  );
}

function PdfIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M7 3.5h7.2L19 8.2V20a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 6 20V5A1.5 1.5 0 0 1 7.5 3.5H7z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M14 3.5V8h4.5" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M8.5 13h7M8.5 16.5h4.5" />
    </svg>
  );
}
